import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  fetchEventLatencyMetrics,
  type EventLatencySnapshot,
} from '../../services/systemHealthService';

function latencySeverity(p95Ms: number): 'success' | 'warning' | 'error' {
  if (p95Ms <= 0) return 'success';
  if (p95Ms < 2000) return 'success';
  if (p95Ms < 5000) return 'warning';
  return 'error';
}

const EventPipelineLatencyWidget: React.FC = () => {
  const [data, setData] = useState<EventLatencySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchEventLatencyMetrics();
      setData(res);
      setError(null);
    } catch {
      setError('이벤트 지연 메트릭을 불러오기 실패');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const p95 = data?.p95_ms ?? 0;
  const severity = latencySeverity(p95);
  const barValue = Math.min(100, (p95 / 5000) * 100);

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">이벤트 파이프라인 지연</Typography>
        <Tooltip title="새로고침">
          <span>
            <IconButton size="small" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        생성→알림→WS 경로 최근 샘플 (최대 200건)
      </Typography>

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && <Alert severity="info">{error}</Alert>}

      {data && (
        <>
          <Typography variant="body2" gutterBottom>
            샘플 수: <strong>{data.count}</strong>
          </Typography>
          <Typography variant="body2" gutterBottom>
            평균: <strong>{data.avg_ms.toFixed(1)} ms</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            P95: <strong>{data.p95_ms.toFixed(1)} ms</strong>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={data.count > 0 ? barValue : 0}
            color={severity}
            sx={{ height: 8, borderRadius: 1 }}
          />
          {data.count === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              아직 샘플 없음. 위험 이벤트가 발생하면 집계됨
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};

export default EventPipelineLatencyWidget;
